import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type PropsWithChildren,
} from 'react';
import { styled } from '@compiled/react';
import {
	SOFTWARE_PROJECT,
	SERVICE_DESK_PROJECT,
} from '@jira/platform__project-types/src/project-types.tsx';
import {
	FREE_EDITION,
	getEditionForProject,
} from '@jira/platform__application-editions/src/edition.tsx';
import { token } from '@atlaskit/tokens';
import { isFedRamp } from '@atlassian/atl-context';
import { AsyncAiUpsellInEditorFreeModal as AIUpsellInEditorFreeModal } from '@atlassian/jira-growth-ai-upsell-in-editor-free-modal/src/ui/async.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useSiteHasOnlyJira } from '@atlassian/jira-billing/src/controllers/explicitly-licensed-products/use-site-has-only-jira.tsx';
import { getUpsellButtonWrapperClicked, setUpsellButtonWrapperClicked } from './utils.tsx';
import type { UseAIUpsellInEditorFreeResult, Product, SupportedProject } from './types.tsx';

const useGetSupportedProjectType = (product: Product): SupportedProject => {
	const siteHasOnlyJira = useSiteHasOnlyJira();

	switch (product) {
		case 'JSW':
			return SOFTWARE_PROJECT;
		case 'JSM':
			return SERVICE_DESK_PROJECT;
		case 'JWM':
			// If tenant is configured with only JWM and JSW, we can provide an upgrade link for JSW
			return siteHasOnlyJira ? SOFTWARE_PROJECT : undefined;
		case 'JPD':
		default:
			return undefined;
	}
};

const useGetSupportedEdition = (product: Product) => {
	const appEditions = useAppEditions();
	const projectType = useGetSupportedProjectType(product);

	if (!appEditions) {
		return undefined;
	}

	if (projectType) {
		return getEditionForProject(projectType, appEditions);
	}

	return undefined;
};

/**
 * This computes the requirements for being eligible for the feature without factoring in optInStatus
 * Exported for use in ../../use-ai-opt-in such that it returns "disabled-opt-in" for FREE sites
 */
export const useAIUpsellInEditorRequirements = (product: Product): boolean => {
	const isOrgAdmin = useIsSiteAdmin();
	const supportedEdition = useGetSupportedEdition(product);

	const siteMeetsRequirements = supportedEdition === FREE_EDITION && isOrgAdmin && !isFedRamp();

	return siteMeetsRequirements;
};

export const useAIUpsellInEditorFree = (
	optInStatus: string,
	product: Product,
): UseAIUpsellInEditorFreeResult => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [showUpsellModal, setShowUpsellModal] = useState(false);

	const siteMeetsRequirements = useAIUpsellInEditorRequirements(product);
	const projectType = useGetSupportedProjectType(product);

	const siteMeetsOptInRequirements = siteMeetsRequirements && optInStatus === 'disabled-opt-in';

	const triggerUpsellFlow = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'triggerUpsellFlow',
				actionSubject: 'aiUpsellInEditorFree',
			}),
			'aiUpsellInEditorFreeTrigger',
			{},
		);

		setShowUpsellModal(true);
		setUpsellButtonWrapperClicked(true);
	}, [createAnalyticsEvent]);

	const UpsellModal = useMemo(
		() =>
			showUpsellModal && projectType ? (
				<AIUpsellInEditorFreeModal
					projectType={projectType}
					testId="platform-react-hooks-use-ai-upsell-in-editor-free.growth-ai-upsell-in-editor-modal"
					onModalClose={() => setShowUpsellModal(false)}
				/>
			) : null,
		[showUpsellModal, projectType],
	);

	return {
		isFeatureEnabled: siteMeetsOptInRequirements,
		triggerUpsellFlow,
		UpsellModal,
	};
};

export const AIUpsellButtonWrapper = ({ children }: PropsWithChildren<{}>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isClicked = getUpsellButtonWrapperClicked();
	const hasFired = useRef(false);

	useEffect(() => {
		if (!hasFired.current) {
			fireUIAnalytics(
				createAnalyticsEvent({}),
				'button viewed',
				'aiUpsellInEditorFreeToolbarButton',
				{},
			);
			hasFired.current = true;
		}
	}, [createAnalyticsEvent]);

	return !isClicked ? (
		<AIUpsellButtonContainer data-testid="platform-react-hooks-use-ai-upsell-in-editor-free.ai-upsell-button-wrapper">
			<AIUpsellButtonInner>{children}</AIUpsellButtonInner>
		</AIUpsellButtonContainer>
	) : (
		<>{children}</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const AIUpsellButtonContainer = styled.div({
	borderRadius: token('radius.small'),
	background: `conic-gradient(
    from 270deg,
    #0065FF 0%,
    #0469FF 20%,
    #BF63F3 50%,
    #FFA900 56%,
    #0065FF 100%
  )`,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	padding: '1px',
	display: 'inline-flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const AIUpsellButtonInner = styled.div({
	backgroundColor: token('elevation.surface'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '3px'),
});
